import { xano } from "@/lib/xano";
import { EngineView } from "@/components/EngineView";
import { StatCard, SectionTitle } from "@/components/shared";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { gbp, num, pct } from "@/lib/format";
import type { OverviewPayload, MarketSnapshot } from "@/lib/engine-types";

function MarketCard({ m }: { m: MarketSnapshot }) {
  const top = (m.top_destinations ?? []).slice(0, 6);
  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <Badge variant="secondary">HS {m.hs6_code}</Badge>
            <p className="mt-2 line-clamp-2 text-sm text-muted-foreground" title={m.hs6_name}>
              {m.hs6_name}
            </p>
          </div>
          <div className="text-right">
            <div className="font-display text-xl font-semibold">{gbp(m.total_value_gbp)}</div>
            <div className="text-xs text-muted-foreground">UK exports, last 12m</div>
          </div>
        </div>
        <div className="mt-3 flex gap-4 text-xs text-muted-foreground">
          <span>
            <strong className="text-foreground">{num(m.exporter_count)}</strong> UK exporters
          </span>
          <span>
            <strong className="text-foreground">{num(m.destination_count)}</strong> destinations
          </span>
        </div>
        <div className="mt-4 space-y-1.5">
          {top.map((dst) => (
            <div key={dst.country_code} className="flex items-center gap-2 text-sm">
              <span className="w-8 shrink-0 font-medium">{dst.country_code}</span>
              <div className="h-2 flex-1 overflow-hidden rounded-full bg-secondary">
                <div className="h-full rounded-full bg-primary" style={{ width: `${dst.share_pct}%` }} />
              </div>
              <span className="w-12 shrink-0 text-right tabular-nums text-muted-foreground">
                {pct(dst.share_pct, 0)}
              </span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function render(raw: unknown) {
  const d = raw as OverviewPayload;
  const snaps = d.market_snapshots ?? [];
  const ta = d.trade_activity;
  const totalMarket = snaps.reduce((s, m) => s + (m.total_value_gbp || 0), 0);

  return (
    <>
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
        <StatCard label="Addressable UK market" value={gbp(totalMarket)} hint="Across your trade codes" />
        <StatCard label="Trade codes" value={num(ta?.hs6_codes?.length ?? snaps.length)} />
        <StatCard
          label="First seen in HMRC data"
          value={ta?.first_year ? String(ta.first_year) : "—"}
          hint={ta?.trader_name || ""}
        />
      </div>

      <SectionTitle>Market intelligence by trade code</SectionTitle>
      <div className="grid gap-4 lg:grid-cols-2">
        {snaps.map((m) => (
          <MarketCard key={m.hs6_code} m={m} />
        ))}
      </div>

      {ta?.activity && ta.activity.length > 0 && (
        <>
          <SectionTitle>Your HMRC trade activity</SectionTitle>
          <div className="grid gap-3 md:grid-cols-2">
            {ta.activity.map((row, i) => (
              <Card key={i}>
                <CardContent className="flex items-start gap-3 p-4">
                  <Badge variant={row.trade_type === "export" ? "default" : "secondary"}>
                    {row.trade_type}
                  </Badge>
                  <div className="min-w-0">
                    <div className="text-sm font-medium">HS {row.hs6_code}</div>
                    <p className="line-clamp-2 text-xs text-muted-foreground" title={row.hs6_description}>
                      {row.hs6_description}
                    </p>
                    <div className="mt-1 text-xs text-muted-foreground">
                      {row.import_months || row.export_months || row.year}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </>
      )}
    </>
  );
}

export default function Markets() {
  return (
    <EngineView
      title="Markets"
      description="HMRC market intelligence for your trade codes, and your own import / export footprint."
      queryKey="overview"
      queryFn={(token) => xano.dashboard.overview(token)}
      render={render}
    />
  );
}
